/* eslint-disable react/no-unescaped-entities */
import {
  ShieldCheck, CreditCard, Lock, Mail, MapPin, Globe2, FileCheck2, BadgeCheck, Building2,
} from "lucide-react";
import { CenteredCarousel } from "@/components/CenteredCarousel";

// REACT_APP_BBB_PROFILE_URL can override the default. We hard-code the
// real URL below so the BBB card shows even without env config.
const BBB_PROFILE_URL = (
  process.env.REACT_APP_BBB_PROFILE_URL
  || "https://www.bbb.org/ca/ab/edmonton/profile/property-management/central-rentals-edmonton-0017-272027"
).trim();

/**
 * Trust & Credentials block — only shows verifications Central Rentals
 * actually holds. BBB row is verifiable (links to real BBB profile page).
 * Drop this anywhere; designed to be a high-impact strip near the bottom
 * of marketing pages.
 */
const BASE_CREDS = [
  { icon: BadgeCheck, label: "Verified Google Business", sub: "Listed and trusted across Canada", color: "emerald" },
  { icon: CreditCard, label: "Stripe-powered payments",   sub: "PCI-DSS Level 1 via Stripe Connect", color: "violet" },
  { icon: Lock,       label: "TLS 1.3 encryption",        sub: "End-to-end secure connections",   color: "sky" },
  { icon: Mail,       label: "DKIM + SPF authenticated",  sub: "Email from centralrentals.ca via Resend", color: "amber" },
  { icon: FileCheck2, label: "CASL-compliant marketing",  sub: "One-click unsubscribe + consent log", color: "rose" },
  { icon: ShieldCheck,label: "PIPEDA-aware data handling",sub: "Canadian privacy law compliance",  color: "teal" },
  { icon: MapPin,     label: "Canadian-owned & operated", sub: "Built in Canada, serving coast to coast", color: "slate" },
  { icon: Globe2,     label: "Service in all 13 provinces", sub: "Coast to coast to coast",        color: "indigo" },
];

const TONE = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  violet:  "bg-violet-50  text-violet-700  border-violet-200",
  sky:     "bg-sky-50     text-sky-700     border-sky-200",
  amber:   "bg-amber-50   text-amber-700   border-amber-200",
  rose:    "bg-rose-50    text-rose-700    border-rose-200",
  teal:    "bg-teal-50    text-teal-700    border-teal-200",
  slate:   "bg-slate-100  text-slate-700   border-slate-200",
  indigo:  "bg-indigo-50  text-indigo-700  border-indigo-200",
  navy:    "bg-blue-50    text-blue-800    border-blue-200",
};

export default function TrustBadges({ compact = false }) {
  // BBB card intentionally REMOVED (Feb 2026) — we are NOT a BBB Accredited
  // Business per bbb.org, so surfacing a "BBB Business Profile" card was
  // misleading. Legitimate trust signals below (Alberta Corporate Registry,
  // Stripe, Google Business Profile, Resend) stay untouched.
  const creds = [...BASE_CREDS];

  const footerNote = "Stripe, Resend and Google logos are trademarks of their respective owners.";

  return (
    <section className={`max-w-6xl mx-auto px-5 md:px-8 ${compact ? "py-10" : "py-14"}`} data-testid="trust-badges">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-2 text-center">Trust &amp; credentials</div>
      <h2 className={`font-display ${compact ? "text-xl md:text-2xl" : "text-2xl md:text-3xl"} font-extrabold tracking-tighter text-slate-900 text-center mb-8`}>
        Every signal Google, Stripe and Canadian regulators look for.
      </h2>
      <CenteredCarousel
        items={creds}
        perViewMd={2}
        perViewLg={3}
        testid="trust"
        renderItem={(c) => {
          const Icon = c.icon;
          const inner = (
            <>
              <div className={`w-12 h-12 rounded-xl border ${TONE[c.color] || TONE.slate} flex items-center justify-center mb-4`}>
                <Icon className="w-6 h-6"/>
              </div>
              <div className="text-base font-bold text-slate-900 leading-tight">{c.label}</div>
              <div className="text-sm text-slate-500 mt-1.5 leading-snug">{c.sub}</div>
            </>
          );
          const baseClass = "p-6 sm:p-7 rounded-xl border bg-white transition-all block h-full min-h-[160px]";
          const testId = `trust-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
          if (c.href) {
            return (
              <a href={c.href} target="_blank" rel="noreferrer noopener"
                className={`${baseClass} border-slate-200 hover:border-blue-300 hover:shadow-sm`}
                data-testid={testId}>
                {inner}
              </a>
            );
          }
          return (
            <div className={`${baseClass} border-slate-200 hover:border-slate-300`} data-testid={testId}>
              {inner}
            </div>
          );
        }}
      />
      <div className="text-[11px] text-slate-500 text-center mt-6 leading-relaxed">{footerNote}</div>
    </section>
  );
}

